import React, { useState, useEffect } from 'react';
import { X, Download, CheckCircle2, AlertCircle, ExternalLink, Monitor, Apple, ChevronDown, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { TAURUS_VERSION } from '../config/taurusVersion';
import { API_ENDPOINTS } from '../config';

/**
 * TaurusInstallModal - Simplified 2-tab design (Windows | Mac)
 * Each tab is self-contained with download, instructions, and troubleshooting
 */
function TaurusInstallModal({ isOpen, onClose, userEmail }) {
  const [activeTab, setActiveTab] = useState('windows');
  const [showTerms, setShowTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  // Collapsible sections
  const [authExpanded, setAuthExpanded] = useState(false);
  const [troubleshootExpanded, setTroubleshootExpanded] = useState(false);
  const [termsExpanded, setTermsExpanded] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('windows');
      setShowTerms(false);
      setTermsAccepted(false);
      setDownloading(false);
      setDownloadError(null);
      setDownloadSuccess(false);
      setAuthExpanded(false);
      setTroubleshootExpanded(false);
      setTermsExpanded(false);
    }
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle download button click
  const handleDownloadClick = () => {
    if (!termsAccepted) {
      setShowTerms(true);
      return;
    }
    executeDownload();
  };

  // Execute the actual download
  const executeDownload = async () => {
    if (!termsAccepted) {
      setDownloadError('Please accept the terms and conditions');
      return;
    }

    setDownloading(true);
    setDownloadError(null);

    try {
      const token = localStorage.getItem('token');

      // Record download
      await axios.post(
        `${API_ENDPOINTS.main}/taurus/download`,
        {
          version: TAURUS_VERSION,
          platform: activeTab
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Trigger download
      const link = document.createElement('a');
      link.href = `${window.location.origin}/downloads/VerbaGPT_Taurus.zip`;
      link.download = 'VerbaGPT_Taurus.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setDownloadSuccess(true);
      setShowTerms(false);

    } catch (error) {
      console.error('Download failed:', error);
      setDownloadError(error.response?.data?.detail || 'Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (!isOpen) return null;

  // Platform-specific content
  const platformContent = {
    windows: {
      icon: Monitor,
      title: 'Windows',
      launcher: 'Start Taurus (Windows).bat',
      pythonInstall: (
        <>
          <li><a href="https://www.python.org/downloads/" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">python.org/downloads</a></li>
          <li>Microsoft Store → search "Python 3.11"</li>
          <li><code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-xs">winget install Python.Python.3.11</code></li>
        </>
      ),
      troubleshooting: [
        {
          problem: '"Python not found"',
          solution: 'Install Python 3.11 from python.org or Microsoft Store'
        },
        {
          problem: '"Port 48002 already in use"',
          solution: 'Right-click Taurus tray icon → Stop Taurus, or restart your computer'
        },
        {
          problem: 'Dependencies fail to install',
          solution: 'Check internet connection. Try running as Administrator.'
        }
      ]
    },
    mac: {
      icon: Apple,
      title: 'Mac',
      launcher: 'Start Taurus (Mac).command',
      pythonInstall: (
        <>
          <li><a href="https://www.python.org/downloads/" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">python.org/downloads</a></li>
          <li><code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-xs">brew install python@3.11</code></li>
        </>
      ),
      troubleshooting: [
        {
          problem: '"Python not found"',
          solution: 'Install Python via Homebrew or python.org'
        },
        {
          problem: '"Port 48002 already in use"',
          solution: <span>Run: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">lsof -ti:48002 | xargs kill -9</code></span>
        },
        {
          problem: 'File won\'t open (security)',
          solution: 'Right-click → Open, or System Preferences → Security → Allow'
        }
      ]
    }
  };

  const platform = platformContent[activeTab];
  const Icon = platform.icon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Install VerbaGPT Taurus
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
              Local AI with full database access
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          {['windows', 'mac'].map(tab => {
            const TabIcon = platformContent[tab].icon;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border-b-2 transition-colors font-medium ${
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <TabIcon className="h-5 w-5" />
                {platformContent[tab].title}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Download Section */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-800 rounded-xl">
                <Icon className="h-8 w-8 text-blue-600 dark:text-blue-300" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 dark:text-white">
                  Download for {platform.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  VerbaGPT_Taurus.zip • 500 KB
                </p>
              </div>

              {!downloadSuccess ? (
                <button
                  onClick={handleDownloadClick}
                  disabled={downloading}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors font-medium"
                >
                  {downloading ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="h-5 w-5" />
                      Download
                    </>
                  )}
                </button>
              ) : (
                <div className="flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg">
                  <CheckCircle2 className="h-5 w-5" />
                  Downloaded!
                </div>
              )}
            </div>

            {/* Inline Terms */}
            {showTerms && !downloadSuccess && (
              <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700">
                {/* Expandable Terms */}
                <div className="mb-3">
                  <button
                    onClick={() => setTermsExpanded(!termsExpanded)}
                    className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {termsExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    {termsExpanded ? 'Hide' : 'View'} Terms & Conditions
                  </button>

                  {termsExpanded && (
                    <div className="mt-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 h-48 overflow-y-auto text-xs text-gray-700 dark:text-gray-300">
                      <div className="space-y-3">
                        <p className="font-bold">
                          By downloading VerbaGPT Taurus, you agree to be bound by these terms of service and all applicable laws.
                        </p>

                        <div>
                          <p className="font-bold">1. Use License</p>
                          <p>Permission is granted to download one copy of VerbaGPT Taurus for personal, non-commercial use. Under this license you may not:</p>
                          <ul className="list-disc pl-4 mt-1 space-y-0.5">
                            <li>Modify or copy the materials</li>
                            <li>Use the materials for any commercial purpose without authorization</li>
                            <li>Attempt to decompile, reverse engineer, or decrypt any encrypted or obfuscated code</li>
                            <li>Remove any copyright or proprietary notations from the materials</li>
                            <li>Transfer the materials to another person or "mirror" on any other server</li>
                          </ul>
                        </div>

                        <div>
                          <p className="font-bold">2. Disclaimer</p>
                          <p>The software is provided on an 'AS IS' basis. VerbaGPT LLC makes no warranties, expressed or implied, and hereby disclaims all warranties including, without limitation, implied warranties of merchantability, fitness for a particular purpose, or non-infringement of intellectual property.</p>
                        </div>

                        <div>
                          <p className="font-bold">3. LIMITATION OF LIABILITY</p>
                          <p className="uppercase text-[10px]">TO THE EXTENT NOT PROHIBITED BY APPLICABLE LAW, VERBAGPT LLC IS NOT LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING BUT NOT LIMITED TO DAMAGES FOR LOSS OF PROFITS, REVENUE, GOODWILL, USE, DATA, OR OTHER ECONOMIC ADVANTAGE.</p>
                        </div>

                        <div>
                          <p className="font-bold">4. User Responsibility</p>
                          <p className="uppercase text-[10px]">YOU HAVE SOLE RESPONSIBILITY FOR ADEQUATE PROTECTION AND BACKUP OF DATA. IT IS THE USER'S RESPONSIBILITY TO REVIEW ANY CODE PRODUCED BY AN LLM PRIOR TO RUNNING OR EXECUTING IT.</p>
                        </div>

                        <div>
                          <p className="font-bold">5. Hold Harmless</p>
                          <p className="uppercase text-[10px]">YOU AGREE TO HOLD VERBAGPT LLC HARMLESS FROM, AND YOU COVENANT NOT TO SUE VERBAGPT LLC FOR, ANY CLAIMS BASED ON USING THE SOFTWARE, WHETHER IN CONTRACT, TORT (INCLUDING NEGLIGENCE) OR OTHERWISE.</p>
                        </div>

                        <div>
                          <p className="font-bold">6. Disclaimer of Warranties</p>
                          <p className="uppercase text-[10px]">YOUR USE OF THE SOFTWARE IS AT YOUR SOLE RISK. THE SOFTWARE IS PROVIDED "AS IS" AND "AS AVAILABLE" BASIS. VERBAGPT LLC DISCLAIMS ALL EXPRESS OR IMPLIED CONDITIONS, REPRESENTATIONS, AND WARRANTIES OF ANY KIND.</p>
                        </div>

                        <div>
                          <p className="font-bold">7. Modifications</p>
                          <p>VerbaGPT LLC may revise these terms at any time without notice. By downloading and using this software, you agree to be bound by the then-current version of these terms.</p>
                        </div>

                        <div>
                          <p className="font-bold">8. Governing Law</p>
                          <p>These terms are governed by and construed in accordance with the laws of Colorado, USA.</p>
                        </div>

                        <p className="text-center text-gray-400 pt-2">— End of Terms —</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Checkbox */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="terms-checkbox" className="text-sm text-gray-700 dark:text-gray-300">
                    I accept the Terms & Conditions and acknowledge that I use this software at my own risk.
                  </label>
                </div>

                {/* Download Button */}
                {termsAccepted && (
                  <button
                    onClick={executeDownload}
                    disabled={downloading}
                    className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors font-medium"
                  >
                    {downloading ? (
                      <>
                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" />
                        Confirm Download
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            {/* Error */}
            {downloadError && (
              <div className="mt-3 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                <AlertCircle className="h-4 w-4" />
                {downloadError}
              </div>
            )}
          </div>

          {/* Quick Start */}
          <div>
            <h4 className="font-bold text-gray-900 dark:text-white mb-3">Quick Start</h4>
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 flex items-center justify-center text-sm font-bold">1</div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Extract ZIP</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Extract to any folder (Desktop, Documents, etc.)</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 flex items-center justify-center text-sm font-bold">2</div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Double-click <code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-sm">{platform.launcher}</code></p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">First run creates isolated environment & installs dependencies (~1-2 min)</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 flex items-center justify-center text-sm font-bold">3</div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Start Taurus</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Right-click system tray icon → Start Taurus → Icon turns green</p>
                </div>
              </div>
            </div>
          </div>

          {/* Prerequisites */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <h4 className="font-bold text-yellow-900 dark:text-yellow-200 mb-3">Prerequisites</h4>

            <div className="space-y-3">
              {/* Python */}
              <div>
                <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-200">Python 3.11+</p>
                <ul className="text-sm text-yellow-800 dark:text-yellow-300 space-y-0.5 ml-4 list-disc">
                  {platform.pythonInstall}
                </ul>
              </div>

              {/* Claude Auth */}
              <div>
                <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-200">Claude Authentication (one of these)</p>
                <ul className="text-sm text-yellow-800 dark:text-yellow-300 space-y-0.5 ml-4 list-disc">
                  <li><a href="https://claude.ai/code" target="_blank" rel="noopener noreferrer" className="underline hover:text-yellow-600 dark:hover:text-yellow-100">Claude Code CLI</a> with active subscription</li>
                  <li>Anthropic API key from <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="underline hover:text-yellow-600 dark:hover:text-yellow-100">console.anthropic.com</a></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Authentication (Collapsible) */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <button
              onClick={() => setAuthExpanded(!authExpanded)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <span className="font-medium text-gray-900 dark:text-white">Authentication Setup</span>
              {authExpanded ? (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronRight className="h-5 w-5 text-gray-500" />
              )}
            </button>
            {authExpanded && (
              <div className="px-4 pb-4 space-y-3">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                  <p className="font-semibold text-sm text-gray-900 dark:text-white mb-2">Option A: Claude Subscription</p>
                  <ol className="text-sm text-gray-700 dark:text-gray-300 space-y-1 ml-4 list-decimal">
                    <li>Install <a href="https://claude.ai/code" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">Claude Code CLI</a></li>
                    <li>Run: <code className="bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded text-xs">claude auth login</code></li>
                    <li>Taurus tray → Authentication → Claude Subscription</li>
                  </ol>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                  <p className="font-semibold text-sm text-gray-900 dark:text-white mb-2">Option B: API Key</p>
                  <ol className="text-sm text-gray-700 dark:text-gray-300 space-y-1 ml-4 list-decimal">
                    <li>Get key from <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">console.anthropic.com</a></li>
                    <li>Taurus tray → Authentication → Configure API Key</li>
                  </ol>
                </div>
              </div>
            )}
          </div>

          {/* Troubleshooting (Collapsible) */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <button
              onClick={() => setTroubleshootExpanded(!troubleshootExpanded)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <span className="font-medium text-gray-900 dark:text-white">Troubleshooting</span>
              {troubleshootExpanded ? (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronRight className="h-5 w-5 text-gray-500" />
              )}
            </button>
            {troubleshootExpanded && (
              <div className="px-4 pb-4 space-y-3">
                {platform.troubleshooting.map((item, idx) => (
                  <div key={idx} className="text-sm">
                    <p className="font-semibold text-gray-900 dark:text-white">{item.problem}</p>
                    <p className="text-gray-600 dark:text-gray-400">{item.solution}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between">
          <div className="flex gap-4 text-sm">
            <a href="https://docs.verbagpt.com" target="_blank" rel="noopener noreferrer" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-1">
              Docs <ExternalLink className="h-3 w-3" />
            </a>
            <a href="mailto:support@verbagpt.com" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-1">
              Support <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default TaurusInstallModal;
